import { useEffect, useState, type FormEvent } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { api, ApiError } from "../api";

/** Settings-and-admin shell for the cloud portal.
 *
 *  Operational work happens inside master_frontend served through the
 *  reverse tunnel at /app/?farm=<id> — this shell only renders when the
 *  user is on /settings/* or /admin/*. A link back to Farm Control sits
 *  at the top of the sidebar so config pages aren't a dead-end.
 */
export function Layout() {
  const { user, logout } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Collapse the mobile drawer on route change — users expect a nav tap
  // to both navigate AND dismiss the drawer, like native mobile apps.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="app">
      {/* Mobile-only top header — desktop hides via CSS. */}
      <div className="mobile-header">
        <button
          className="hamburger"
          aria-label="Mở menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((o) => !o)}
        >
          ☰
        </button>
        <span className="brand">🌿 Irrigation Cloud</span>
      </div>

      {drawerOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav className={`sidebar${drawerOpen ? " open" : ""}`}>
        <div className="sidebar-brand">🌿 Irrigation Cloud</div>

        <a href="/" className="sidebar-link" style={{ fontWeight: 500, marginBottom: 8 }}>
          🚀 Farm Control
        </a>

        <div style={{ marginTop: 12 }}>
          <SectionLabel>CẤU HÌNH</SectionLabel>
          <NavLink to="/settings/farms" className={linkClass}>⚙ Settings</NavLink>
          <NavLink to="/settings/lorawan" className={linkClass}>📡 LoRaWAN</NavLink>
        </div>

        {user?.is_platform_admin && (
          <div style={{ marginTop: 12 }}>
            <SectionLabel>PLATFORM ADMIN</SectionLabel>
            <NavLink to="/admin/owners" className={linkClass}>🛡 Owners</NavLink>
          </div>
        )}

        <div className="sidebar-footer">
          <div>{user?.email}</div>
          <div>
            <span className={`badge ${user?.is_platform_admin ? "admin" : "owner"}`}>
              {user?.is_platform_admin ? "admin" : user?.role}
            </span>
          </div>
          <button
            onClick={() => setShowChangePw(true)}
            style={{ marginTop: 12, width: "100%", fontSize: "0.85rem" }}
          >
            Đổi mật khẩu
          </button>
          <button
            onClick={logout}
            style={{ marginTop: 6, width: "100%", fontSize: "0.85rem" }}
          >
            Đăng xuất
          </button>
        </div>
      </nav>

      <main className="content">
        <Outlet />
      </main>

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}

function linkClass({ isActive }: { isActive: boolean }) {
  return `sidebar-link${isActive ? " active" : ""}`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em",
        color: "var(--muted)", padding: "4px 12px 6px", textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Change-password modal (unchanged from previous Layout)
// ═══════════════════════════════════════════════════════════════════

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPw !== confirmPw) {
      setError("Mật khẩu mới và xác nhận không khớp");
      return;
    }
    if (newPw.length < 8) {
      setError("Mật khẩu mới phải từ 8 ký tự");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setSuccess(true);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Đổi mật khẩu thất bại");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h2>Đổi mật khẩu</h2>
        {success ? (
          <>
            <div className="alert ok">Mật khẩu đã được cập nhật.</div>
            <div className="btn-row">
              <button type="button" className="primary" onClick={onClose}>Đóng</button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            {error && <div className="alert error">{error}</div>}
            <div className="field">
              <label>Mật khẩu hiện tại</label>
              <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required autoFocus autoComplete="current-password" />
            </div>
            <div className="field">
              <label>Mật khẩu mới (min 8)</label>
              <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="field">
              <label>Xác nhận</label>
              <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div className="btn-row">
              <button type="button" onClick={onClose}>Huỷ</button>
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Đang đổi…" : "Đổi mật khẩu"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
